import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorHandlerService } from 'app/core-lib/services/error-handler/error-handler.service';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { CoreLibSessionRevokedModalComponent } from 'app/core-lib/dialogs/session-revoked-modal/session-revoked-modal.component';
import { LogoutReason } from './session/logout-reason';
import { SESSION_OVERLAY_BACKDROP_CLASSES, SESSION_OVERLAY_PANEL_CLASSES } from './session/session-overlay';
import { SessionTerminationService } from './session/session-termination.service';

const SESSION_TAKEN_OVER_CODE = 1402;

const UNAUTHORIZED_STATUS = 'UNAUTHORIZED';

interface HttpErrorLike {
  status?: number | string;
  error?: { status?: string };
}

@Injectable({ providedIn: 'root' })
export class SessionRevokedService {
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly modalService = inject(ModalService);
  private readonly sessionTermination = inject(SessionTerminationService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  matches(error: unknown): boolean {
    if (this.errorHandler.getErrorCode(error) !== SESSION_TAKEN_OVER_CODE) return false;
    const { status, error: body } = (error ?? {}) as HttpErrorLike;
    return status === 401 || status === UNAUTHORIZED_STATUS || body?.status === UNAUTHORIZED_STATUS;
  }

  handle(): void {
    if (this.sessionTermination.isTerminating) return;
    if (!localStorage.getItem('currentJWT')) return;
    if (this.router.url.startsWith('/login')) return;

    this.zone.run(() => {
      this.sessionTermination.begin();
      try {
        this.modalService.closeAll();
        this.modalService
          .open(
            CoreLibSessionRevokedModalComponent,
            { data: { noCloseOnBackdropClick: true } },
            SESSION_OVERLAY_PANEL_CLASSES,
            SESSION_OVERLAY_BACKDROP_CLASSES,
          )
          .afterClosed()
          .subscribe(() => this.sessionTermination.end(LogoutReason.SessionRevoked));
      } catch (error) {
        console.error('[session] could not display the revoked session notice:', error);
        this.sessionTermination.end(LogoutReason.SessionRevoked);
      }
    });
  }
}
